import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { physioStatusApi } from '../services/api';
import { Card } from '../components/Common';
import type { PhysioStatus as PhysioStatusType } from '../types';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const STATUS_OPTIONS: { value: PhysioStatusType; label: string; description: string; color: string }[] = [
  { value: 'active', label: 'Active', description: 'Available for work', color: 'bg-green-100 border-green-500 text-green-800' },
  { value: 'sick', label: 'Sick', description: 'Off sick, not available', color: 'bg-red-100 border-red-500 text-red-800' },
  { value: 'unavailable', label: 'Unavailable', description: 'Not available for scheduling', color: 'bg-gray-100 border-gray-500 text-gray-800' },
];

export function PhysioStatus() {
  const { statusToken } = useParams<{ statusToken: string }>();
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data: statusInfo, isLoading, error } = useQuery({
    queryKey: ['physioStatus', statusToken],
    queryFn: async () => {
      const response = await physioStatusApi.getStatus(statusToken as string);
      return response.data;
    },
    enabled: !!statusToken,
  });

  const updateMutation = useMutation({
    mutationFn: (newStatus: PhysioStatusType) => physioStatusApi.updateStatus(statusToken as string, newStatus),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['physioStatus', statusToken] });
      setSuccessMessage(`Status updated to "${response.data.status}"`);
      setTimeout(() => setSuccessMessage(null), 3000);
    },
  });

  if (!statusToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full text-center">
          <p className="text-red-600">Invalid status link.</p>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !statusInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Link Not Found</h2>
          <p className="text-gray-600">
            This status link is invalid or has expired. Please contact your administrator for a new link.
          </p>
        </Card>
      </div>
    );
  }

  const currentStatus = STATUS_OPTIONS.find((s) => s.value === statusInfo.status) || STATUS_OPTIONS[0];

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-lg mx-auto space-y-6">
        <Card>
          <div className="text-center space-y-4">
            <h1 className="text-2xl font-bold text-gray-900">Hi, {statusInfo.physio_name}</h1>
            <p className="text-gray-600">Update your availability status below.</p>
          </div>
        </Card>

        <Card>
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Current Status</h2>
              <div className={`inline-block px-4 py-2 rounded-lg border-2 ${currentStatus.color}`}>
                <span className="font-medium">{currentStatus.label}</span>
                <span className="text-sm ml-2 opacity-75">- {currentStatus.description}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Last updated: {formatDate(statusInfo.updated_at)}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Change Status</h2>

            {successMessage && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800">
                {successMessage}
              </div>
            )}

            {updateMutation.isError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800">
                Failed to update status. Please try again.
              </div>
            )}

            <div className="grid gap-3">
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => updateMutation.mutate(option.value)}
                  disabled={updateMutation.isPending || statusInfo.status === option.value}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    statusInfo.status === option.value
                      ? `${option.color} border-opacity-100`
                      : 'bg-white border-gray-200 hover:border-gray-400'
                  } ${updateMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{option.label}</p>
                      <p className="text-sm text-gray-600">{option.description}</p>
                    </div>
                    {statusInfo.status === option.value && (
                      <span className="text-green-600 font-medium">Current</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </Card>

        <p className="text-center text-sm text-gray-500">
          This link is personal to you. Do not share it with others.
        </p>
      </div>
    </div>
  );
}
